import React, { FC, useEffect } from 'react';

import {
  Box,
  Flex,
  Modal,
  Button,
  ModalBody,
  IconButton,
  SimpleGrid,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import { HiMinusCircle } from 'react-icons/hi';

import { Form, Formik, useFormikContext } from 'formik';

import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';

import { selectors } from 'store/slices/washhub';

import { initialValues, validationSchema } from './AddItemModal.utils';

import { SKU } from 'types/sku';
import { AddItemModalViewProps } from './AddItemModal.props';
import getSortedData from 'utils/getSortedData';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const AddItemModalView: FC<AddItemModalViewProps> = (props) => {
  const { skus, isOpen, onClose, onSubmit, isLoading, isLoadingSKU } = props;

  const washHub = selectors.useSelectedWashHub();

  return (
    <Modal
      size="6xl"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader textAlign="center" fontWeight="500">
          Add new item
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" px={6} pb={6}>
            <Formik
              onSubmit={onSubmit}
              initialValues={initialValues}
              validationSchema={validationSchema}>
              {({ values, setFieldValue }) => (
                <Form>
                  <Flex>
                    <FormikSelectInput
                      placeholder="Select"
                      label="SKU (*)"
                      isLoading={isLoadingSKU}
                      containerProps={{ flex: 1 }}
                      name="sku"
                      labelProps={labelProps}>
                      {getSortedData(skus)?.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s?.name || ''}
                        </option>
                      ))}
                    </FormikSelectInput>
                    <Box h="20px" w="20px" />
                    <FormikInput
                      name="washHub"
                      label="Wash Hub"
                      labelProps={labelProps}
                      isReadOnly
                      value={washHub?.name || ''}
                      containerProps={{ flex: 1 }}
                    />
                    <Box h="20px" w="20px" />
                    <FormikInput
                      name="sku"
                      label="SKU ID (*)"
                      isReadOnly
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                      bg="#f1f1f1"
                    />
                  </Flex>
                  <Flex mt={4}>
                    <SkuSize skus={skus} />
                    <Box h="20px" w="20px" />
                    <SkuType skus={skus} />
                  </Flex>
                  <SimpleGrid columns={2}>
                    {values.items.map((d, i) => (
                      <Flex
                        key={`-${i}`}
                        mt={6}
                        position="relative"
                        alignItems="flex-end">
                        <FormikInput
                          id={`count-${i}`}
                          name={`items[${i}]`}
                          label="Item ID (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1 }}
                        />
                        <Box h="20px" w="10px" />
                        {i !== 0 && (
                          <IconButton
                            aria-label="remove-item"
                            icon={<HiMinusCircle size="25px" fill="#E54444" />}
                            size="sm"
                            variant="ghost"
                            position="absolute"
                            right={3}
                            mb={1}
                            onClick={() =>
                              setFieldValue(
                                'items',
                                values?.items?.filter((_, j) => j !== i),
                              )
                            }
                          />
                        )}
                      </Flex>
                    ))}
                  </SimpleGrid>
                  <Flex flex={1} mt={8} w="100%">
                    {values?.items?.length <= 7 && (
                      <Button
                        w="100%"
                        size="lg"
                        borderRadius="5px"
                        variant="outline"
                        bg="primary.900"
                        onClick={() =>
                          setFieldValue('items', [...values?.items, ''])
                        }>
                        Add Item (max 8)
                      </Button>
                    )}
                  </Flex>
                  <Flex my={12} flexDirection="row" justifyContent="center">
                    <Button
                      variant="outline"
                      w="134px"
                      marginRight="10px"
                      onClick={onClose}
                      isDisabled={isLoading}>
                      Cancel
                    </Button>
                    <Button type="submit" w="134px" isLoading={isLoading}>
                      Create
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddItemModalView;

type Props = { skus: SKU[] };

const SkuSize: FC<Props> = ({ skus }) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const skuId = values?.sku;

  useEffect(() => {
    const _sku = skus?.find((s) => s._id === skuId);

    setFieldValue('size', _sku?.size || '');
  }, [skuId]);

  return (
    <FormikInput
      name="size"
      label="Size"
      isReadOnly
      labelProps={labelProps}
      containerProps={{ flex: 1 }}
      bg="#f1f1f1"
    />
  );
};

const SkuType: FC<Props> = ({ skus }) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const skuId = values?.sku;

  useEffect(() => {
    const _sku = skus?.find((s) => s._id === skuId);

    setFieldValue('type', _sku?.type || '');
  }, [skuId]);

  return (
    <FormikInput
      name="type"
      label="Items Type"
      isReadOnly
      labelProps={labelProps}
      containerProps={{ flex: 1 }}
      bg="#f1f1f1"
    />
  );
};
